import { LegacyRef, useEffect, useRef, useState } from 'react'
import { Badge } from '@mui/material'
import DOMPurify from 'dompurify'
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable'
import { useMeasure, useHoverDirty, useUpdate } from 'react-use'
import { useHasEllipsis } from '@global/hooks'
import { cn } from '@global/utils/tailwind'
import { t } from '@lingui/core/macro'
import { NavList, ListType } from './navList'
import * as css from './styles'
import { Status, TypeEditableTitle } from './types'

interface HeaderNavProps {
  title?: string | null
  links?: ListType[]
  status?: Status | null
  onBlurCallback?: (value: string) => void
  onFocusCallback?: () => void
  editableTitleCallback?: TypeEditableTitle | null
  editableTitleRequired?: boolean
  editableDefaultTitle?: string
  titleValidationError?: boolean
  children?: React.ReactNode
}

/**
 * Show the error message; animated.
 * @param props React props
 * @param {boolean} props.show Show the error.
 * @returns
 */
const TitleRequiredErrorMessage = ({ show }: { show: boolean }) => {
  if (!show) return null

  const requiredErrorVariant = {
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.01,
      },
    },
    hidden: {
      opacity: 0,
    },
  }

  const requiredErrorVariantChildren = {
    visible: {
      opacity: 1,
    },
    hidden: {
      opacity: 0,
    },
  }

  const MESSAGE = 'Submission name is required.'

  return (
    <css.ErrorMessage variants={requiredErrorVariant} initial="hidden" animate="visible">
      {MESSAGE.split('').map((c, index) => (
        <css.AnimatedCharacter
          key={`${c}-${String(index)}`}
          variants={requiredErrorVariantChildren}
        >
          {c}
        </css.AnimatedCharacter>
      ))}
    </css.ErrorMessage>
  )
}

const TitleWithTooltip = ({ title }: { title: string }) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const element = textRef.current
    if (!element) return

    const checkTruncation = () => {
      setIsTruncated(element.offsetWidth < element.scrollWidth)
    }

    const resizeObserver = new ResizeObserver(checkTruncation)
    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const withTooltipProps = isTruncated
    ? {
        'data-tooltip': title,
        'data-tooltip-dir': 'bottom',
      }
    : {}

  return (
    <css.Title ref={textRef} {...withTooltipProps}>
      {title}
    </css.Title>
  )
}

/**
 * Header component.
 * - Template Title
 * - Or Submission Name; which can be editable.
 * - Navigation
 * @param props React Props
 * @param {string} props.title Page title or Template name.
 * @param {ListType[]} props.links Navigation links
 * @param {Status} props.status Status badge
 * @param {Function} props.onBlurCallback Function fired during the onBlur event of the title change.
 * @param {Function} props.onFocusCallback Function fired during the onFocus event of the title change.
 * @param {TypeEditableTitle} props.editableTitleCallback Function called on editable title change.
 * @param {boolean} props.editableTitleRequired The editable title is required.
 * @param {string} props.editableDefaultTitle The default title of the editable title element.
 * @param {boolean} props.titleValidationError Title is invalid. IMPORTANT; validation logic should be external to
 * this component since it can vary.
 * @returns
 */
export const HeaderNav = ({
  title = null,
  links = [],
  status = null,
  editableTitleCallback = null,
  onBlurCallback = () => void {},
  onFocusCallback = () => void {},
  editableTitleRequired = false,
  titleValidationError = false,
  editableDefaultTitle = '',
  children,
}: HeaderNavProps) => {
  const hoverRef = useRef(null)
  const update = useUpdate()
  const isTitleHovered = useHoverDirty(hoverRef)
  const [headerRef, { width: headerWidth }] = useMeasure()
  const sanitizedTitle = title ? DOMPurify.sanitize(title, { USE_PROFILES: { html: false } }) : null
  const trackTitleText = useRef(sanitizedTitle ?? editableDefaultTitle)

  const isTitleRequiredError = editableTitleRequired && titleValidationError

  const titleRef = useRef<HTMLDivElement | null>(null)

  const hasTitleEllipsis = useHasEllipsis({
    parentElRef: hoverRef,
    childElRef: titleRef,
    fontSize: `${String(css.TITLE_FONT_SIZE)}px`,
    deps: [trackTitleText.current, hoverRef.current, titleRef.current],
  })

  const titleEditableClasses = cn({
    '-active-required-error': isTitleRequiredError,
    '-is-hovered': isTitleHovered,
  })

  const headerClasses = cn('flex-row', {
    '-no-navlist': links.length === 0,
  })

  // Update trackTitleText ref value if title changes from parent company (e.g. after loading)
  useEffect(() => {
    if (!sanitizedTitle) return
    if (sanitizedTitle === trackTitleText.current) return

    trackTitleText.current = sanitizedTitle
    update()
  }, [sanitizedTitle])

  // Captured pasted data from the editable title, and sanitize user input.
  useEffect(() => {
    const $editalbeHeader = document.getElementById('header-submissionname-editable')
    $editalbeHeader?.addEventListener('paste', handlePaste)

    return () => {
      $editalbeHeader?.removeEventListener('paste', handlePaste)
    }

    function handlePaste(e: ClipboardEvent) {
      let newSubmissionName: string

      // Stop data actually being pasted into div
      e.stopPropagation()
      e.preventDefault()

      const currentSubmissionName = trackTitleText.current

      // Get caret position when user initiated paste
      const selection = window.getSelection()
      const range = selection?.getRangeAt(0)
      const caretStart = range?.startOffset
      const caretEnd = range?.endOffset

      // Get pasted data via clipboard API
      const pastedData = e.clipboardData?.getData(t`Text`)

      const sanitizedPastedData = DOMPurify.sanitize(pastedData ?? '', {
        USE_PROFILES: { html: false },
      })

      // overwrite submission name substring if substring intentionally highlighted
      const rangeLength = range?.toString().length ?? 0
      if (rangeLength > 0) {
        const highlightStart = currentSubmissionName.slice(0, caretStart)
        const highlightEnd = currentSubmissionName.slice(caretEnd)
        const submissionNameMinusHighlight = [highlightStart, highlightEnd].join('')

        newSubmissionName = [
          submissionNameMinusHighlight.slice(0, caretStart),
          sanitizedPastedData,
          submissionNameMinusHighlight.slice(caretStart),
        ].join('')
      } else {
        newSubmissionName = [
          currentSubmissionName.slice(0, caretStart),
          sanitizedPastedData,
          currentSubmissionName.slice(caretStart),
        ].join('')
      }

      trackTitleText.current = newSubmissionName
      if (editableTitleCallback !== null) {
        editableTitleCallback(newSubmissionName)
      }
      update() // re-render to show latest useRef for title.
    }
  }, [update])

  /**
   * Track each change using useRef values to allow the title to be cleared
   * using backspace and prevents the default title from replacing the value.
   * - Calls editableTitleCallback to notify parent containers of the title change.
   * @param event Html Change Event.
   */
  function trackTitleChanges(event: ContentEditableEvent) {
    const value = event.target.value
    trackTitleText.current = value.replace(/&nbsp;/g, ' ')
  }

  /**
   * Reset header title if user clicks away from input and it's empty.
   */
  function onBlur() {
    if (trackTitleText.current !== '') {
      // Blur callback is typically used for saving submission name,
      // only call if a change was made.
      onBlurCallback(trackTitleText.current)
      if (editableTitleCallback !== null) {
        editableTitleCallback(sanitizeInput(trackTitleText.current))
      }
      return
    }

    trackTitleText.current = title ?? editableDefaultTitle
    onBlurCallback(trackTitleText.current)
    if (editableTitleCallback !== null) {
      editableTitleCallback(sanitizeInput(trackTitleText.current))
    }
    update()
  }

  /**
   * Block enter key press within editable title from starting new line.
   * - Fire callbacks to trigger save on Enter.
   * - Blur focus to prevent an additional save when user clicks away.
   */
  function onKeyUp(e: React.KeyboardEvent) {
    const ENTER_KEY = 'Enter'
    if (e.key === ENTER_KEY) {
      e.preventDefault()
      onBlurCallback(trackTitleText.current)
      if (editableTitleCallback !== null) {
        editableTitleCallback(sanitizeInput(trackTitleText.current))
      }
      const $editalbeHeader = document.getElementById('header-submissionname-editable')
      $editalbeHeader?.blur()
    }
  }

  /**
   * When element is focused, if text is the default text select the whole text.
   * - Was not able to clear the text and set the caret position; this may still be
   * possible by positioning the caret after the component re-renders (useEffect)
   * @returns
   */
  function onFocus() {
    onFocusCallback()
    if (trackTitleText.current !== editableDefaultTitle) return

    const $editalbeHeader = document.getElementById('header-submissionname-editable')
    if (!$editalbeHeader?.childNodes[0]) return

    const range = document.createRange()
    range.selectNodeContents($editalbeHeader.childNodes[0])
    const sel = document.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  function sanitizeInput(value: string): string {
    const sanitizedInput = DOMPurify.sanitize(value, { USE_PROFILES: { html: false } })
    return sanitizedInput
  }

  const contentEditableDataAttrs = {
    id: 'header-submissionname-editable',
    'data-test-id': 'header-submissionname-editable',
    'data-test-page-id': 'header-submissionname-editable',
  }

  const isEditableElementFocused = document.activeElement === titleRef.current
  const showTooltip = hasTitleEllipsis && !isEditableElementFocused

  const showTitleWrapper = title !== null || editableDefaultTitle !== ''

  return (
    <css.Header ref={headerRef as LegacyRef<HTMLElement>} className={headerClasses}>
      <div className="flex-3">
        {showTitleWrapper && (
          <css.TitleWrapper>
            {editableTitleCallback === null ? (
              <TitleWithTooltip title={title ?? editableDefaultTitle} />
            ) : (
              <css.TitleEditable ref={hoverRef} className={titleEditableClasses}>
                <div
                  data-tooltip={showTooltip ? trackTitleText.current : null}
                  data-tooltip-dir="bottom"
                >
                  <ContentEditable
                    {...contentEditableDataAttrs}
                    html={trackTitleText.current}
                    disabled={false}
                    onChange={trackTitleChanges}
                    onBlur={onBlur}
                    onKeyUp={onKeyUp}
                    onFocus={onFocus}
                    tagName="div"
                    innerRef={titleRef}
                  />
                </div>
              </css.TitleEditable>
            )}
            {status && (
              <Badge css={[css.badge, status === Status.UPDATE_ROWS ? css.badgeBlue : null]}>
                {status}
              </Badge>
            )}
            <TitleRequiredErrorMessage show={isTitleRequiredError} />
          </css.TitleWrapper>
        )}
        {links.length > 0 && <NavList list={links} parentWidth={headerWidth} />}
      </div>
      <div className="flex-1">{children}</div>
    </css.Header>
  )
}
